import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAlternativeNames, updateAlternativeNames } from 'services'
import { Modal, PlainTable } from 'stories/components'
import { borrowerFullName, capitalize, confirm, InputConvert, InputValidate, prompt } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { BorrowerSeperator } from '../config'
import type { IAlternativeName } from './types'

const typeOptions: Record<string, string> = {
  both: 'Credit Alias and AKA',
  credit: 'Credit Alias Only',
  aka: 'AKA Only',
}

const defaultInputs = (): Record<string, InputType> => {
  return {
    firstName: {
      inputType: 'text',
      title: 'First Name',
      value: '',
      required: true,
    },
    middleName: {
      inputType: 'text',
      title: 'Middle Name',
      value: '',
      required: false,
    },
    lastName: {
      inputType: 'text',
      title: 'Last Name',
      value: '',
      required: true,
    },
    suffix: {
      inputType: 'text',
      title: 'Suffix',
      value: '',
      required: false,
    },
    creditorName: {
      inputType: 'text',
      title: 'Creditor Name',
      value: '',
      required: false,
    },
    accountNo: {
      inputType: 'text',
      title: 'Account No',
      required: false,
    },
    type: {
      inputType: 'select',
      title: 'Type',
      options: typeOptions,
      required: false,
      hasDefaultOption: true,
    },
  }
}

const AlternativeNamesForm = (props: { data: IAlternativeName | null; onSubmit: Function }) => {
  const [inputs, setInputs] = useState(defaultInputs())

  useEffect(() => {
    if (!props.data) return

    const newInputs = cloneDeep(inputs)
    Object.keys(inputs).forEach((key) => {
      newInputs[key].value = (props.data as any)[key]
    })
    setInputs(newInputs)
  }, [props.data])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    props.onSubmit({
      ...props.data,
      ...data,
    })
  }

  return (
    <Modal
      title={`${props.data ? 'Update' : 'Add'} Alternative Name`}
      titleOkay={'Submit'}
      onClose={() => props.onSubmit(null)}
      onOk={onSubmit}
      isOpen
    >
      <div className="w-[calc(100vw-5rem)] max-w-[36rem]">
        <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
          {Object.keys(inputs).map((key, index) => {
            const input = inputs[key]

            return (
              <div className={`input md:col-span-${input.span || 1}`} key={index}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

export const AlternativeNamesModal = (props: { borrowerSeperator: BorrowerSeperator; onClose: Function }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<IAlternativeName[]>([])
  const [renderType, setRenderType] = useState(0)
  const [formId, setFormId] = useState<Number>()

  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('Template.AlternativeNames')

  const { borrower } = useSelector((state: any) => ({
    borrower: state.borrower,
  }))

  useEffect(() => {
    fetch()
  }, [])

  const getCondition = () => {
    const { firstName, middleName, lastName } = borrower[props.borrowerSeperator]
    return { firstName, middleName, lastName }
  }

  const fetch = async () => {
    setLoading(true)

    const data = await getAlternativeNames(getCondition())
    setData(data)
    setLoading(false)
  }

  const onClose = () => {
    props.onClose()
  }

  const onOk = () => {
    if (renderType == 0) {
      setRenderType(1)
      setFormId(0)
      return
    }
  }

  const onSubmitForm = (newItem: IAlternativeName | null) => {
    setRenderType(0)
    if (newItem == null) {
      setFormId(undefined)
      return
    }

    const newData = cloneDeep(data)
    const index = formId ? data.findIndex((v) => v.id == formId) : -1
    if (index == -1)
      newData.push({
        ...newItem,
        id: Date.now(),
      })
    else newData[index] = newItem
    update(newData)
  }

  const onDelete = async (item: IAlternativeName, index: number) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure to remove this {borrowerFullName(item)}?
        <br />
        <span className="text-gray-600">No: {index + 1}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(data)
    newData.splice(index, 1)
    update(newData)
  }

  const update = async (data: IAlternativeName[]) => {
    setData(data)

    setLoading(true)
    await updateAlternativeNames(getCondition(), data)
    setLoading(false)
  }

  const onImportNames = async () => {
    const res: any = await prompt('Add New Alternative Names', { rows: 10 })
    if (res === false) return
    const newData = cloneDeep(data)
    const names = res.split('\n')
    names.map((name: string, index: number) => {
      const splits = name
        .split(' ')
        .filter((v) => !!v)
        .map((v) => capitalize(v))
      if (splits.length < 2 || splits.length > 3) return

      const newItem = {
        id: Date.now() + index,
        firstName: splits[0],
        middleName: splits.length > 2 ? splits[1] : '',
        lastName: splits[splits.length - 1],
      }

      let isExists = false
      newData.map((item) => {
        if (
          item.firstName == newItem.firstName &&
          ((!item.middleName && !newItem.middleName) || item.middleName === newItem.middleName) &&
          item.lastName == newItem.lastName
        ) {
          isExists = true
          return
        }
      })
      if (isExists) return
      newData.push(newItem)
    })
    update(newData)
  }

  const renderTable = () => {
    const name = getCondition()
    return (
      <Modal
        title={`Alternative Names (${borrowerFullName(name)})`}
        titleOkay={canEdit ? 'Add' : ''}
        loading={loading}
        onClose={onClose}
        onOk={onOk}
        isOpen
        titleCancel="Close"
      >
        <div className="w-[calc(100vw-5rem)] max-w-[41rem]">
          <div className="flex justify-end mb-2">
            <span className="text-[14px] cursor-pointer hover:underline text-shade-blue p-1" onClick={onImportNames}>
              + Add Multiple Names
            </span>
          </div>
          <PlainTable
            header={['No', 'Type', 'First Name', 'Middle Name', 'Last Name', canEdit ? 'Actions' : ''].filter(
              (v) => !!v,
            )}
            data={data.map((item, no) =>
              [
                no + 1,
                item.type ? typeOptions[item.type] : ' ',
                item.firstName,
                item.middleName || ' ',
                item.lastName,
                canEdit ? (
                  <div className="flex gap-1">
                    <span
                      className="rounded p-2 text-shade-blue cursor-pointer hover-shadow1 sm:p-1"
                      onClick={() => {
                        setFormId(item.id)
                        setRenderType(1)
                      }}
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </span>
                    <span
                      className="rounded p-2 text-red-700 cursor-pointer hover-shadow1 sm:p-1"
                      onClick={() => onDelete(item, no)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                ) : (
                  ''
                ),
              ].filter((v) => !!v),
            )}
          />
        </div>
      </Modal>
    )
  }

  const renderForm = () => {
    const index = formId ? data.findIndex((v) => v.id == formId) : -1
    if (index == -1) return <AlternativeNamesForm data={null} onSubmit={onSubmitForm} />

    return <AlternativeNamesForm data={data[index]} onSubmit={onSubmitForm} />
  }

  return renderType == 0 ? renderTable() : renderForm()
}
